import React, { useContext, useState } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import { IconButton, Tooltip, useMediaQuery } from "@material-ui/core";
import { useThemeContext } from "../../context/DarkMode";
import { MoreVert, Replay, CheckCircle, PlayArrow, OpenInNew } from "@material-ui/icons";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import TicketOptionsMenu from "../TicketOptionsMenu";
import ButtonWithSpinner from "../ButtonWithSpinner";
import ContactStateModal from "../ContactStateModal";
import ResolveTicketDialog from "../ResolveTicketDialog";
import SelectQueueOnAcceptModal from "../SelectQueueOnAcceptModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SettingsContext } from "../../context/Settings/SettingsContext";
import { pooledModeFromSetting } from "../../helpers/canOpenTicket";
import { resolveTakeoverQueue } from "../../helpers/takeoverQueue";
import { useFloatingChat } from "../../context/FloatingChat";
import useContactState from "../../hooks/useContactState";

const useStyles = makeStyles(theme => ({
  actionButtons: {
    marginRight: 6,
    flex: "none",
    alignSelf: "center",
    marginLeft: "auto",
    "& > *": {
      margin: theme.spacing(1),
    },
    // feat_142: en <1280px (compact) los botones se aprietan (gap mucho menor).
    [theme.breakpoints.down("md")]: {
      marginRight: 2,
      "& > *": {
        margin: theme.spacing(0.25),
      },
    },
  },
}));

const TicketActionButtons = ({ ticket }) => {
  const classes = useStyles();
  const theme = useTheme();
  // feat_142: <1280px → DEVOLVER/RESOLVER/ATENDER/REABRIR como IconButton + tooltip.
  // feat_328: la densidad "compact" activa el modo compacto también en
  // pantallas grandes (el viewport solo ya no alcanza como criterio).
  const { density } = useThemeContext();
  const isCompact =
    useMediaQuery(theme.breakpoints.down("md")) || density === "compact";
  const history = useHistory();
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [lostModalOpen, setLostModalOpen] = useState(false);
  // feat_345: elegir a qué cola va un cerrado ajeno que estoy tomando.
  const [selectQueueModalOpen, setSelectQueueModalOpen] = useState(false);
  const { user } = useContext(AuthContext);
  const { getSetting } = useContext(SettingsContext);
  const { openChat } = useFloatingChat();

  // feat_270: mueve la conversación a una ventana flotante persistente.
  const handleOpenFloating = () => {
    const name =
      ticket?.contact?.name ||
      ticket?.contact?.whatsappName ||
      ticket?.contact?.number ||
      String(ticket?.id);
    openChat(ticket.id, name);
    history.push("/tickets");
  };

  const {
    currentState,
    states,
    lostReasons,
    loading: stateLoading,
    changeState,
  } = useContactState(ticket?.contact?.id, ticket?.contact?.contactState);

  const ticketOptionsMenuOpen = Boolean(anchorEl);

  const handleOpenTicketOptionsMenu = e => setAnchorEl(e.currentTarget);
  const handleCloseTicketOptionsMenu = () => setAnchorEl(null);

  const doResolve = async () => {
    setLoading(true);
    try {
      await api.put(`/tickets/${ticket.id}`, { status: "closed", userId: user?.id || null });
      history.push("/tickets");
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
  };

  const handleResolveClick = () => {
    // feat_343: llave "Resolver directo" por empresa (default off) → cierra sin menú
    // ni cambio de estado del contacto. doResolve() es el MISMO cierre que usa hoy la
    // rama "sin cambio" del menú: mismo PUT, mismo farewell, mismos resolvedAt/By.
    if (getSetting("directResolve") === "true") {
      doResolve();
      return;
    }
    // If already Ganado (4) or Perdido (5), resolve directly
    const systemId = currentState?.systemId;
    if (systemId === 4 || systemId === 5) {
      doResolve();
      return;
    }
    // Otherwise show the dialog
    setResolveDialogOpen(true);
  };

  const handleResolveGanado = async () => {
    setResolveDialogOpen(false);
    setLoading(true);
    try {
      // Find systemId=4 state
      const ganadoState = states.find(s => s.systemId === 4);
      if (ganadoState) {
        await changeState(ganadoState.id);
      }
    } catch (err) {
      toastError(err);
    }
    await doResolve();
  };

  const handleResolveLost = () => {
    setResolveDialogOpen(false);
    setLostModalOpen(true);
  };

  // Bridge between shared ResolveTicketDialog and existing single-flow handlers
  const handleResolveType = (type) => {
    if (type === "ganado") return handleResolveGanado();
    if (type === "perdido") return handleResolveLost();
    // sin_cambio
    setResolveDialogOpen(false);
    doResolve();
  };

  // feat_345 — la regla de "a qué cola va el cerrado que estoy tomando" ya no
  // vive acá: es `helpers/takeoverQueue`, compartida con el ATENDER de vista
  // previa de components/Ticket. Reemplaza al `buildTakeoverQueuePatch` de
  // feat_338, que la tenía escrita en este archivo y copiada en el otro.
  //
  // `overrideQueueId` es el 4º parámetro y sólo lo manda la re-entrada desde el
  // modal; `e` no se usa en el cuerpo, así que pasar null ahí es seguro. Los
  // otros 3 botones que comparten este handler (ATENDER un pending, DEVOLVER a
  // la cola) no lo mandan y el helper les devuelve "none".
  const handleUpdateTicketStatus = async (e, status, userId, overrideQueueId) => {
    let queueIdFinal = overrideQueueId;

    if (overrideQueueId === undefined) {
      const dec = resolveTakeoverQueue({
        ticket,
        user,
        pooledMode: pooledModeFromSetting(
          getSetting("closedTicketsPooledByQueue")
        ),
        status
      });
      // El modal corta ANTES del setLoading: si el spinner arranca acá y el
      // usuario cancela, el botón queda deshabilitado para siempre (el
      // setLoading(false) sólo vive en el try/catch del PUT).
      if (dec.mode === "ask") {
        setSelectQueueModalOpen(true);
        return;
      }
      if (dec.mode === "auto") queueIdFinal = dec.queueId;
    }

    setLoading(true);
    try {
      await api.put(`/tickets/${ticket.id}`, {
        status,
        userId: userId || null,
        // Sin queueId el backend conserva la cola actual (undefined lo ignora
        // Sequelize); mandar null dejaría el ticket huérfano de cola.
        ...(queueIdFinal !== undefined ? { queueId: queueIdFinal } : {})
      });
      setLoading(false);
      if (status === "open") {
        history.push(`/tickets/${ticket.id}`);
      } else {
        history.push("/tickets");
      }
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
  };

  return (
    <div className={classes.actionButtons}>
      <Tooltip title={i18n.t("floatingChat.openFloating")} arrow>
        <IconButton size="small" onClick={handleOpenFloating}>
          <OpenInNew fontSize="small" />
        </IconButton>
      </Tooltip>
      {ticket.status === "closed" && (
        isCompact ? (
          <Tooltip title={i18n.t("messagesList.header.buttons.reopen")} arrow>
            <span>
              <IconButton
                size="small"
                disabled={loading}
                onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
              >
                <Replay fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <ButtonWithSpinner
            loading={loading}
            startIcon={<Replay />}
            size="small"
            onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
          >
            {i18n.t("messagesList.header.buttons.reopen")}
          </ButtonWithSpinner>
        )
      )}
      {ticket.status === "open" && (
        <>
          <Tooltip title="Devolver a la cola de espera" arrow>
            <span>
              {isCompact ? (
                <IconButton
                  size="small"
                  disabled={loading}
                  onClick={e => handleUpdateTicketStatus(e, "pending", null)}
                >
                  <Replay fontSize="small" />
                </IconButton>
              ) : (
                <ButtonWithSpinner
                  loading={loading}
                  startIcon={<Replay />}
                  size="small"
                  onClick={e => handleUpdateTicketStatus(e, "pending", null)}
                >
                  {i18n.t("messagesList.header.buttons.return")}
                </ButtonWithSpinner>
              )}
            </span>
          </Tooltip>
          <Tooltip title="Marcar como resuelta" arrow>
            <span>
              {isCompact ? (
                <IconButton
                  size="small"
                  color="primary"
                  disabled={loading}
                  onClick={handleResolveClick}
                >
                  <CheckCircle fontSize="small" />
                </IconButton>
              ) : (
                <ButtonWithSpinner
                  loading={loading}
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={handleResolveClick}
                >
                  {i18n.t("messagesList.header.buttons.resolve")}
                </ButtonWithSpinner>
              )}
            </span>
          </Tooltip>
          <Tooltip title="Más opciones" arrow>
            <IconButton onClick={handleOpenTicketOptionsMenu}>
              <MoreVert />
            </IconButton>
          </Tooltip>
          <TicketOptionsMenu
            ticket={ticket}
            anchorEl={anchorEl}
            menuOpen={ticketOptionsMenuOpen}
            handleClose={handleCloseTicketOptionsMenu}
          />
        </>
      )}
      {ticket.status === "pending" && (
        isCompact ? (
          <Tooltip title={i18n.t("messagesList.header.buttons.accept")} arrow>
            <span>
              <IconButton
                size="small"
                color="primary"
                disabled={loading}
                onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
              >
                <PlayArrow fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <ButtonWithSpinner
            loading={loading}
            size="small"
            variant="contained"
            color="primary"
            onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
          >
            {i18n.t("messagesList.header.buttons.accept")}
          </ButtonWithSpinner>
        )
      )}

      {/* Dialog "¿En qué quedó?" — extraído a ResolveTicketDialog (compartido con batch) */}
      <ResolveTicketDialog
        open={resolveDialogOpen}
        onClose={() => setResolveDialogOpen(false)}
        count={1}
        loading={loading}
        onResolve={handleResolveType}
      />

      {/* Modal de motivo de pérdida */}
      <ContactStateModal
        open={lostModalOpen}
        onClose={() => setLostModalOpen(false)}
        contactId={ticket?.contact?.id}
        currentState={currentState}
        states={states}
        lostReasons={lostReasons}
        loading={stateLoading}
        changeState={changeState}
        onStateChanged={async () => {
          setLostModalOpen(false);
          await doResolve();
        }}
        forceStep="lost"
      />

      {/* feat_345 — a qué cola va el cerrado ajeno que estoy tomando. El modal
          NO se cierra solo: lo cierran onConfirm y onClose. Cancelar no dispara
          ningún PUT, el ticket sigue cerrado. */}
      <SelectQueueOnAcceptModal
        open={selectQueueModalOpen}
        onClose={() => setSelectQueueModalOpen(false)}
        onConfirm={queueId => {
          setSelectQueueModalOpen(false);
          handleUpdateTicketStatus(null, "open", user?.id, queueId);
        }}
        userQueues={user?.queues || []}
      />
    </div>
  );
};

export default TicketActionButtons;
